import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Card, CardContent, Typography, Button, Grid, Box, Alert,
  CircularProgress, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Autocomplete,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import styled from '@emotion/styled';
import { sessionAPI, medicineAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const PatientInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

function SessionDetails() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { isDoctor } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [medRow, setMedRow] = useState({ medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' });

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      const response = await sessionAPI.getById(sessionId);
      setSession(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch session details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintInvoice = () => {
    const printWindow = window.open('', '_blank');

    const sessionCost = parseFloat(session.cost) || 0;
    const amountBeforeGST = sessionCost / 1.05;
    const gstAmount = sessionCost - amountBeforeGST;
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;

    const patient = session.treatment?.patient;
    const treatment = session.treatment;

    const patientAddress = patient ? [patient.street, patient.city, patient.state, patient.zipCode].filter(Boolean).join(', ') : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - Session ${session.sessionNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A5; margin: 0; }
          body {
            font-family: 'Arial', sans-serif;
            width: 148mm;
            height: 210mm;
            padding: 8mm;
            margin: 0 auto;
            font-size: 9pt;
          }
          .bill {
            border: 1px solid #000;
            padding: 6mm;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          .header {
            border-bottom: 1px dashed #000;
            padding-bottom: 4mm;
            margin-bottom: 4mm;
          }
          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 3mm;
          }
          .header-logo {
            width: 25mm;
            height: auto;
            object-fit: contain;
          }
          .header-center {
            flex: 1;
            text-align: center;
            padding: 0 3mm;
          }
          .header-center h1 {
            font-size: 13pt;
            margin-bottom: 1mm;
            font-weight: bold;
            line-height: 1.2;
          }
          .header-center h2 {
            font-size: 9pt;
            margin-bottom: 2mm;
            font-weight: 600;
            line-height: 1.2;
          }
          .header-center .address {
            font-size: 7pt;
            line-height: 1.3;
            margin: 0;
          }
          .bill-details {
            text-align: center;
            margin-top: 2mm;
          }
          .bill-details p {
            font-size: 8pt;
            margin: 0.5mm 0;
            line-height: 1.2;
          }
          .section { margin: 3mm 0; }
          .section-title {
            font-weight: bold;
            font-size: 9pt;
            border-bottom: 1px solid #000;
            margin-bottom: 2mm;
            padding-bottom: 1mm;
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin: 1.5mm 0;
            font-size: 8pt;
            line-height: 1.3;
          }
          .info-row { justify-content: flex-start; gap: 2mm; }
          .info-row span:first-child { font-weight: bold; }
          .patient-info-container { display: flex; justify-content: space-between; align-items: flex-start; }
          .patient-info-right { text-align: left; max-width: 50%; font-size: 8pt; line-height: 1.3; }
          .items-table {
            width: 100%;
            margin: 3mm 0;
            border-collapse: collapse;
          }
          .items-table th {
            text-align: left;
            border-bottom: 1px solid #000;
            padding: 2mm 0;
            font-size: 8pt;
            font-weight: bold;
          }
          .items-table td {
            padding: 1.5mm 0;
            font-size: 8pt;
            line-height: 1.2;
          }
          .items-table .item-name { width: 50%; }
          .items-table .item-qty { width: 10%; text-align: center; }
          .items-table .item-price { width: 15%; text-align: right; }
          .items-table .item-gst { width: 10%; text-align: right; font-size: 7pt; }
          .items-table .item-total { width: 15%; text-align: right; }
          .totals {
            border-top: 1px dashed #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          .totals .row { font-size: 9pt; margin: 1.5mm 0; }
          .totals .final-total {
            font-size: 11pt;
            font-weight: bold;
            border-top: 1px solid #000;
            padding-top: 2mm;
            margin-top: 2mm;
          }
          .footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 3mm;
            margin-top: auto;
            font-size: 7pt;
            line-height: 1.3;
          }
          @media print {
            body {
              padding: 8mm;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none; }
          }
          @media screen {
            body {
              background: #f0f0f0;
              padding: 10mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="bill">
          <div class="header">
            <div class="header-top">
              <img src="/logo.jpg" alt="Logo" class="header-logo" />
              <div class="header-center">
                <h1>GLOW with SuperDoc</h1>
                <h2>MSG Hotels & Resorts</h2>
                <p class="address">Opp. MSG International School</p>
                <p class="address">Nezia Khera, Sirsa</p>
                <p class="address">Haryana, India</p>
                <p class="address">Pin Code 125055</p>
                <p class="address">GST NO: 06AAJCA4867D4ZL</p>
              </div>
              <img src="/logo2.png" alt="Logo" class="header-logo" />
            </div>
             <div class="bill-details">
              <p><strong>Treatment Session Invoice</strong></p>
              <p>Invoice No: SES-${session.id}</p>
              <p>Date: ${new Date(session.sessionDate).toLocaleDateString()} ${new Date(session.createdAt || session.sessionDate || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</p>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Patient Details</div>
            <div class="patient-info-container">
              <div>
                <div class="row info-row"><span>Name:</span><span>${patient?.firstName} ${patient?.lastName}</span></div>
                <div class="row info-row"><span>Phone:</span><span>${patient?.phone}</span></div>
                <div class="row info-row"><span>Age/Gender:</span><span>${calculateAge(patient?.dateOfBirth)} years / ${patient?.gender}</span></div>
              </div>
              ${patientAddress ? `<div class="patient-info-right"><strong>Address:</strong> ${patientAddress}</div>` : ''}
            </div>
          </div>

          <div class="section">
            <div class="section-title">Treatment Session Details</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th class="item-name">Description</th>
                  <th class="item-qty">Qty</th>
                  <th class="item-price">Rate</th>
                  <th class="item-gst">GST (5%)</th>
                  <th class="item-total">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="item-name">${treatment?.treatmentType} - Session ${session.sessionNumber}</td>
                  <td class="item-qty">1</td>
                  <td class="item-price">₹${Math.round(amountBeforeGST).toLocaleString('en-IN')}</td>
                  <td class="item-gst">₹${Math.round(gstAmount).toLocaleString('en-IN')}</td>
                  <td class="item-total">₹${Math.round(sessionCost).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="totals">
            <div class="row"><span>Subtotal (Before GST):</span><span>₹${Math.round(amountBeforeGST).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>CGST (2.5%):</span><span>₹${Math.round(cgst).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>SGST (2.5%):</span><span>₹${Math.round(sgst).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>Total GST (5%):</span><span>₹${Math.round(gstAmount).toLocaleString('en-IN')}</span></div>
            <div class="row final-total"><span>Total Amount:</span><span>₹${Math.round(sessionCost).toLocaleString('en-IN')}</span></div>
          </div>

          <div class="footer">
            <p>Thank you for your visit!</p>
            <p>Please keep this invoice for your records</p>
          </div>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Invoice</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; margin-left: 10px;">Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const fetchMedicines = async () => {
    try {
      const res = await medicineAPI.getAll({ limit: 1000 });
      setMedicines(res.data.data || []);
    } catch { }
  };

  const openEdit = () => {
    setEditData({
      sessionDate: dayjs(session.sessionDate),
      performedBy: session.performedBy || '',
      cost: session.cost || '',
      notes: session.notes || '',
      nextSessionDate: session.nextSessionDate ? dayjs(session.nextSessionDate) : null,
      prescribedMedicines: session.prescribedMedicines ? session.prescribedMedicines.map(m => ({ ...m })) : [],
    });
    setMedRow({ medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' });
    setEditError('');
    setEditOpen(true);
    fetchMedicines();
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    setEditError('');
    try {
      await sessionAPI.update(sessionId, {
        ...editData,
        sessionDate: editData.sessionDate?.toISOString(),
        nextSessionDate: editData.nextSessionDate?.toISOString() || null,
      });
      setEditOpen(false);
      fetchSessionDetails();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setEditLoading(false);
    }
  };

  const addMedRow = () => {
    if (!medRow.medicineName || !medRow.frequency) {
      setEditError('Medicine name and frequency are required');
      return;
    }
    setEditData({ ...editData, prescribedMedicines: [...editData.prescribedMedicines, { ...medRow }] });
    setMedRow({ medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' });
    setEditError('');
  };

  const removeMed = (idx) =>
    setEditData({ ...editData, prescribedMedicines: editData.prescribedMedicines.filter((_, i) => i !== idx) });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !session) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Session not found</Alert>
      </Box>
    );
  }

  const patient = session.treatment?.patient;
  const treatment = session.treatment;
  const latestVisitation = patient?.visitations?.[0];

  return (
    <Box sx={{ p: 3 }}>
      {/* Screen View Actions */}
      <Box className="no-print" sx={{ mb: 3 }}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print Session Details
              </Button>
              {session.cost && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintInvoice}
                >
                  Print Invoice (₹{parseFloat(session.cost).toFixed(2)})
                </Button>
              )}
              {isDoctor() && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<EditIcon />}
                  onClick={openEdit}
                >
                  Edit Session
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/treatment/${treatment?.id}`)}
              >
                Back to Treatment
              </Button>
            </Box>
          </CardContent>
        </StyledCard>
      </Box>

      {/* Printable View */}
      <div className="prescription-print" style={{ marginTop: '29%' }}>
        <table style={{ width: '100%', marginBottom: '25px' }}>
          <tbody>
            <tr>
              <td><div><strong>Patient:</strong> {patient?.firstName} {patient?.lastName}</div></td>
              <td style={{ textAlign: 'right' }}><div><strong>Date:</strong> {formatDate(session.sessionDate)}</div></td>
            </tr>
            <tr>
              <td>
                <div style={{ marginTop: '8px' }}><strong>Age / Gender:</strong> {calculateAge(patient?.dateOfBirth)} years / {patient?.gender}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {latestVisitation && (
          <>
            {latestVisitation.chiefComplaint && (
              <div style={{ marginBottom: '1rem' }}>
                <h3>Chief Complaint:</h3>
                <p>{latestVisitation.chiefComplaint}</p>
              </div>
            )}
            {latestVisitation.diagnosis && (
              <div style={{ marginBottom: '1rem' }}>
                <h3>Diagnosis:</h3>
                <p>{latestVisitation.diagnosis}</p>
              </div>
            )}
          </>
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <h3>Session Information</h3>
          <p><strong>Session Number:</strong> {session.sessionNumber}</p>
          <p><strong>Treatment Plan:</strong> {treatment?.treatmentType}</p>
          {session.performedBy && <p><strong>Performed By:</strong> {session.performedBy}</p>}
        </div>

        {session.prescribedMedicines && session.prescribedMedicines.length > 0 && (
          <div className="prescription-medicines">
            <h3>Prescribed Medicines</h3>
            {session.prescribedMedicines.map((med, index) => (
              <p key={index} style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
                {index + 1}. {med.medicineName} {med.dosage} {med.frequency} {med.duration} {med.instructions ? `- ${med.instructions}` : ''}
              </p>
            ))}
          </div>
        )}

        {session.notes && (
          <div style={{ marginTop: '2rem' }}>
            <h3>Session Notes</h3>
            <p>{session.notes}</p>
          </div>
        )}

        {session.nextSessionDate && (
          <div style={{ marginTop: '2rem' }}>
            <h3>Next Session</h3>
            <p><strong>Scheduled for:</strong> {formatDate(session.nextSessionDate)}</p>
          </div>
        )}

        <div className="prescription-footer">
          <p>_________________________</p>
          <p>Doctor's Signature</p>
          <p>Date: {formatDate(session.sessionDate)}</p>
        </div>
      </div>

      {/* Screen View Content */}
      <div className="no-print">
        <StyledCard>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
              Session #{session.sessionNumber} - Treatment Plan: {treatment?.treatmentType}
            </Typography>

            <PatientInfoCard>
              <Typography variant="h6" gutterBottom>
                Patient Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Name:</strong> {patient?.firstName || 'N/A'} {patient?.lastName || ''}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography><strong>Age:</strong> {patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : 'N/A'} years</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography><strong>Gender:</strong> {patient?.gender || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Phone:</strong> {patient?.phone || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </PatientInfoCard>

            {latestVisitation && (
              <Paper sx={{ p: 3, mb: 3, backgroundColor: 'info.lighter' }}>
                <Typography variant="h6" gutterBottom>
                  Latest Consultation Info
                </Typography>
                {latestVisitation.chiefComplaint && (
                  <Typography paragraph>
                    <strong>Chief Complaint:</strong> {latestVisitation.chiefComplaint}
                  </Typography>
                )}
                {latestVisitation.diagnosis && (
                  <Typography paragraph>
                    <strong>Diagnosis:</strong> {latestVisitation.diagnosis}
                  </Typography>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Session Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Session Number:</strong> {session.sessionNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Date:</strong> {formatDate(session.sessionDate)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Treatment Plan:</strong> {treatment?.treatmentType}</Typography>
                </Grid>
                {session.performedBy && (
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Performed By:</strong> {session.performedBy}</Typography>
                  </Grid>
                )}
                {session.cost && (
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Cost:</strong> ₹{parseFloat(session.cost).toFixed(2)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {session.prescribedMedicines && session.prescribedMedicines.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Prescribed Medicines
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Medicine</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Dosage</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Frequency</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Duration</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Instructions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {session.prescribedMedicines.map((med, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}><strong>{med.medicineName}</strong></td>
                          <td style={{ padding: '12px' }}>{med.dosage}</td>
                          <td style={{ padding: '12px' }}>{med.frequency}</td>
                          <td style={{ padding: '12px' }}>{med.duration || '-'}</td>
                          <td style={{ padding: '12px' }}>{med.instructions || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            )}

            {session.notes && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Session Notes
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{session.notes}</Typography>
              </Paper>
            )}

            {session.nextSessionDate && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Next Session
                </Typography>
                <Typography><strong>Scheduled for:</strong> {formatDate(session.nextSessionDate)}</Typography>
              </Paper>
            )}
          </CardContent>
        </StyledCard>
      </div>

      {/* ── Edit Session Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Session #{session?.sessionNumber}</DialogTitle>
        <DialogContent dividers>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          {editData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Session Date & Time *" value={editData.sessionDate}
                    onChange={v => setEditData({ ...editData, sessionDate: v })} sx={{ width: '100%' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Performed By" size="small"
                    value={editData.performedBy}
                    onChange={e => setEditData({ ...editData, performedBy: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Cost (₹)" size="small" type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={editData.cost}
                    onChange={e => setEditData({ ...editData, cost: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Next Session Date (Optional)" value={editData.nextSessionDate}
                    onChange={v => setEditData({ ...editData, nextSessionDate: v })} sx={{ width: '100%' }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Session Notes" size="small" multiline rows={3}
                    value={editData.notes}
                    onChange={e => setEditData({ ...editData, notes: e.target.value })} />
                </Grid>
              </Grid>

              {/* Prescribed Medicines */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Prescribed Medicines</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Autocomplete freeSolo size="small"
                      options={medicines.map(m => m.name)}
                      value={medRow.medicineName}
                      onChange={(_, v) => setMedRow({ ...medRow, medicineName: v || '' })}
                      onInputChange={(_, v) => setMedRow({ ...medRow, medicineName: v })}
                      renderInput={(params) => <TextField {...params} label="Medicine" />} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Dosage" value={medRow.dosage}
                      onChange={e => setMedRow({ ...medRow, dosage: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Frequency *" value={medRow.frequency}
                      onChange={e => setMedRow({ ...medRow, frequency: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Duration" value={medRow.duration}
                      onChange={e => setMedRow({ ...medRow, duration: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Instructions" value={medRow.instructions}
                      onChange={e => setMedRow({ ...medRow, instructions: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button fullWidth variant="contained" size="small" startIcon={<AddIcon />} onClick={addMedRow}>
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {editData.prescribedMedicines.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Medicine</TableCell>
                      <TableCell>Dosage</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Instructions</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editData.prescribedMedicines.map((med, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{med.medicineName}</TableCell>
                        <TableCell>{med.dosage}</TableCell>
                        <TableCell>{med.frequency}</TableCell>
                        <TableCell>{med.duration || '-'}</TableCell>
                        <TableCell>{med.instructions || '-'}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeMed(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SessionDetails;
